import { translation } from '@/i18n';
import type { fr } from './fr';

export const en = translation<typeof fr>()({
  checkConnection: 'Check your connection, then try again.',
  'kind.label': 'Vehicle type',
  categories: 'Categories',
  perDay: ' / day',
  models: { one: '{count} model', other: '{count} models' },
  modelsMore: { one: 'More than {count} model', other: 'More than {count} models' },
  seats: { one: '{count} seat', other: '{count} seats' },
  specs: { one: '{transmission} · {fuel} · {count} seat', other: '{transmission} · {fuel} · {count} seats' },
  'error.vehicles': 'Couldn’t load the vehicles',
  'reserve.cta': 'Book',
  'reserve.chooseReturn': 'Choose a return date.',
  'similar.error': 'Suggestions unavailable',
  deliveryHint: 'The agency brings the vehicle to you',

  'search.place': 'Pickup location',
  'search.placeAria': 'Pickup location: {city}. Edit the search',
  'search.pickup': 'Pickup',
  'search.pickupAria': 'Pickup: {when}. Edit the search',
  'search.return': 'Return',
  'search.returnAria': 'Return: {when}. Edit the search',
  'search.dates': 'Dates',
  'search.datesAria': 'Dates: {dates}. Edit the search',
  'search.submit': 'Search',
  'time.earlier': '{label} earlier',
  'time.later': '{label} later',
  'grace.late': 'Return from {time}: an extra day is charged.',
  'grace.ok': 'Return before {time}: no extra day.',
  'calendar.chooseReturn': 'Choose the return',
  'calendar.restart': 'Tap a date to start again',

  'hero.imageAlt': 'Track in the Ourika valley facing the snow-capped peaks of the High Atlas',
  'hero.tag': 'Verified agencies in 20 Moroccan cities',
  'hero.titleLead': 'Your Morocco,',
  'hero.titleAccent': 'keys in hand.',
  'hero.lede':
    'Cars and motorbikes from verified agencies, from the airport to the medina. The final price is shown before you book, and your card is only charged once the agency confirms.',
  'hero.tick.delivery': 'Delivery to your address',
  'hero.tick.refund': 'Full refund up to 48 h before',
  'hero.tick.support': '24/7 support',
  'trust.agencies': 'Agencies vetted by Cardnd',
  'trust.charge': 'Charged only on confirmation',
  'trust.price': 'Total price shown, fees included',
  'showcase.open': 'See the vehicles available in {city}',
  'showcase.live': 'Available now · {city}',
  'showcase.note': '<b>Card pre-authorised</b>, charged only when the agency confirms',

  'categories.title': 'Find the right <serif>vehicle.</serif>',
  'categories.error': 'Couldn’t load the categories',
  'categories.allMotos': 'All motorbikes',
  'categories.from': 'from {price} / day',
  'rail.live': 'Live',
  'rail.title': 'Available in <serif>{city}.</serif>',
  'rail.lead': 'Total price for {days} ({dates}), shown before you book.',
  'rail.all': 'See all',
  'how.eyebrow': 'How it works',
  'how.title': 'Book on Cardnd, <serif>with no nasty surprises.</serif>',
  'how.lead':
    'You book a model. Cardnd sends your request to verified agencies; the first one to confirm hands you the vehicle.',
  'how.1.title': 'Choose your model',
  'how.1.text': 'The total price is shown before you book, service fee included. No surprises at the counter.',
  'how.2.title': 'A verified agency confirms',
  'how.2.text': 'Your card is pre-authorised, not charged. It is only charged when the agency accepts.',
  'how.3.title': 'Show your voucher',
  'how.3.text':
    'QR code and PIN code at the agency, or delivery to your address. The deposit is handled by the agency.',
  'teaser.pill': 'New · Experiences',
  'teaser.title': 'Beyond the road, <serif>Morocco to experience.</serif>',
  'teaser.text': 'Agafay desert, Ourika valley, Toubkal… Experiences led by local guides, bookable in a few minutes.',
  'teaser.cta': 'Discover experiences',
  'teaser.from': 'from {price} {unit}',
  'destinations.eyebrow': 'Destinations',
  'destinations.title': 'Wherever you <serif>land.</serif>',
  'destinations.hint': 'Number of vehicles available per city',
  'destinations.error': 'Couldn’t load the destinations',
  'partner.eyebrow': 'Rental agencies',
  'partner.title': 'Rent out your fleet <serif>without chasing customers.</serif>',
  'partner.text':
    'Receive requests that are already pre-authorised, accept the ones that suit you and hand over the vehicle on presentation of the voucher.',
  'partner.cities': 'cities covered in Morocco',
  'partner.first': '1<sup>st</sup>',
  'partner.firstText': 'agency to accept wins the booking',
  'partner.all': '100%',
  'partner.allText': 'of requests pre-authorised',

  'results.filters': 'Filters',
  'results.datesUpdated': 'Dates updated: {dates}',
  'results.title': '{models} <serif>in {city}</serif>',
  'results.lead': 'Total price for {days}, shown before you book · verified agency assigned on confirmation',
  'results.empty.title': 'No model matches',
  'results.empty.reset': 'Reset the filters',
  'results.empty.change': 'Change dates or city',
  'results.empty.filtered': 'Widen your filters, or change dates or city.',
  'results.empty.text': 'No vehicle is available in {city} on these dates. Try other dates or another city.',
  'results.more': 'See more models',
  'results.moreError': 'The next models couldn’t be loaded: try again.',
  'results.note.title': 'You book a model, not a random agency.',
  'results.note.text':
    'Cardnd sends your request to verified agencies in {city}. The first one to confirm hands you the vehicle, and your card is only charged at that moment.',
  'filters.both': 'Both',
  'filters.close': 'Close the filters',
  'filters.gearbox': 'Gearbox',
  'filters.sort': 'Sort by',
  'filters.clear': 'Clear all',
  'filters.show': 'Show {models}',
  'filters.showResults': 'Show results',
  'sort.priceAsc': 'Price: low to high',
  'sort.priceDesc': 'Price: high to low',
  'sort.depositAsc': 'Lowest deposit',
  'longStay.title': 'Stay 7 days, pay less per day',
  'longStay.text': 'The long-stay discount applies automatically from 7 days, then from 28 days.',
  'longStay.cta': 'Switch to 7 days',
  'seo.results.car': 'Car rental in {city}',
  'seo.results.moto': 'Motorbike rental in {city}',
  'seo.results.carCategory': 'Car rental in {city} · {category}',
  'seo.results.motoCategory': 'Motorbike rental in {city} · {category}',
  'seo.results.description':
    'Vehicles available in {city}: total price shown before you book, verified rental agencies, secure payment.',
  'seo.results.descriptionCount':
    '{models} available in {city}: total price shown before you book, verified rental agencies, secure payment.',

  'vehicle.gone.title': 'This vehicle is no longer available',
  'vehicle.gone.cta': 'See available vehicles',
  'vehicle.gone.text': 'The agency may have removed it. Other models are waiting for you in {city}.',
  'vehicle.error.title': 'Couldn’t show this vehicle',
  'vehicle.loading': 'Loading the vehicle',
  'vehicle.crumb': 'Breadcrumb',
  'vehicle.crumbResults': 'Results in {city}',
  'vehicle.where': 'in {city} · or similar, depending on the agency that confirms',
  'vehicle.booking': 'Booking',
  'seo.vehicle.title': '{name} rental in {city}',
  'seo.vehicle.description': {
    one: 'Rent {vehicle} in {city} from a verified agency: {count} seat, {transmission}, from {price} MAD per day. Your card is only charged on confirmation.',
    other:
      'Rent {vehicle} in {city} from a verified agency: {count} seats, {transmission}, from {price} MAD per day. Your card is only charged on confirmation.',
  },
  'jsonLd.description': {
    one: '{name} for rent in {city}: {count} seat, {transmission}, {fuel}. Verified agency assigned on confirmation.',
    other:
      '{name} for rent in {city}: {count} seats, {transmission}, {fuel}. Verified agency assigned on confirmation.',
  },
  'jsonLd.home': 'Home',
  'jsonLd.results': 'Rental in {city}',
  'reserve.checkFailed': 'Availability couldn’t be checked. Check your connection, then try again.',
  'reserve.signedIn': 'Signed in',
  'reserve.minStay': {
    one: 'Minimum {count} day for this vehicle: extend the rental.',
    other: 'Minimum {count} days for this vehicle: extend the rental.',
  },
  'gallery.similar': 'or similar model',
  'gallery.share': 'Share',
  'gallery.copied': 'Link to this vehicle copied.',
  'gallery.alt1': 'High Atlas road',
  'gallery.alt2': 'Medina alley',

  'card.tier': { one: 'From {count} day: {price} / day', other: 'From {count} days: {price} / day' },
  'card.deposit': 'Deposit',
  'card.toChoose': 'Select a date',
  'card.yourDates': 'Your dates',
  'card.period': 'Period',
  'card.booked': 'Already booked',
  'card.conflictDay': 'This model is already booked on {day}: choose other dates.',
  'card.conflict': 'This model isn’t available on these dates: choose other dates.',
  'card.longStay': 'Long-stay discount',
  'card.notCharged': 'You won’t be charged yet',
  'meter.max': '{days} days or more',
  'meter.progress': '{days} / {target} days',
  'meter.add': 'Add {days}: the discount applies automatically.',
  'meter.applied': 'Discount applied. From {days} days, the discount grows further.',
  'meter.maxApplied': 'Maximum discount applied.',

  'included.title': 'What this rental includes',
  'agency.title': 'Your agency',
  'agency.assigned': 'Assigned on confirmation',
  'agency.oneOf': 'A verified agency in {city}',
  'agency.text':
    'You book the model. Cardnd sends your request to the available partner agencies; the first one to confirm sends you its booking voucher. If none confirms, the pre-authorisation is released.',
  'agency.checked': 'Vetted agencies',
  'agency.voucher': 'QR voucher + PIN code',
  'agency.chat': 'Messages through Cardnd',
  'agency.zone': '{city} · approximate area',
  'agency.zoneText': 'Exact address shared after booking. Delivery to your address available.',
  'policies.title': 'Policies',
  'policies.cancel': 'Cancellation',
  'policies.for': 'for a pickup on {day} at {time}',
  'policies.error': 'Terms unavailable',
  'policies.rules': 'Agency rules',
  'reviews.title': 'Reviews',
  'reviews.hint': 'Rated after each completed rental',
  'reviews.average': 'Average rating',
  'reviews.count': { one: '{count} review', other: '{count} reviews' },
  'reviews.empty.title': 'No reviews yet',
  'reviews.empty.text': 'No reviews for this model in {city} yet. After your rental, you’ll rate five criteria.',
  'reviews.notRated': 'not rated yet',
  'criteria.service': 'Service',
  'criteria.condition': 'Vehicle condition',
  'criteria.value': 'Value for money',
  'criteria.performance': 'Performance',
  'criteria.cleanliness': 'Cleanliness',
  'similar.title': 'Also available in {city}',

  'times.title': 'Confirm your times',
  'times.daysEstimate': '{days} · estimate',
  'times.pickupTime': 'Pickup time',
  'times.returnTime': 'Return time',
  'times.billed': 'Billed duration',
  'times.extraDay': '+1 day',
  'times.graceLate': 'Return from {time}: an extra day is charged (2 h grace after the pickup time).',
  'times.graceOk': 'Return before {time}: no extra day (2 h grace after the pickup time).',
  'times.noSlot': 'No pickup times left today.',
  'times.tomorrow': 'Leave tomorrow',
  'times.past': 'This time has passed: choose a pickup from {time}.',

  'alt.blackoutTitle': 'No bookings on these dates',
  'alt.title': 'This model is already booked',
  'alt.changeDates': 'Change dates',
  'alt.all': 'All vehicles in {city}',
  'alt.blackout': 'Cardnd doesn’t take bookings for this period in {city}. Choose other dates.',
  'alt.text':
    '{name} is no longer available on your dates. These models are free in {city}, on the same dates and times.',
  'alt.loading': 'Looking for available models',
  'alt.none': 'No other model free in {city}',
  'alt.noneText': 'Try other dates or other times.',

  'signIn.eyebrow': 'Last step before payment',
  'signIn.title': 'Sign in to book',
  'signIn.text': 'Your bookings, vouchers and payments are linked to your account. Your dates and times are kept.',
  'signIn.email': 'Email',
  'signIn.emailPlaceholder': 'you@example.com',
  'signIn.password': 'Password',
  'signIn.passwordPlaceholder': 'Your password',
  'signIn.forgot': 'Forgot password?',
  'signIn.submit': 'Sign in and continue',
  'signIn.noAccount': 'No account yet?',
  'signIn.error.emailRequired': 'Enter your email.',
  'signIn.error.emailInvalid': 'Invalid email format.',
  'signIn.error.passwordRequired': 'Enter your password.',

  'sheet.eyebrow': 'Search',
  'sheet.title': 'Where and when are you going?',
  'sheet.show': { one: 'Show {count} model', other: 'Show {count} models' },
  'sheet.hours': 'Times',
  'sheet.delivery': 'Delivery to my address',
});
